import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ModalContext } from 'src/contexts/ModalContext';
import {
  useNewGroupId,
  useOpenAutoPricingForGroupId,
  useSetNewGroupId,
  useSetOpenAutoPricingForGroupId,
} from 'src/contexts/NewGroupScrollContext/NewGroupScroll.atoms';
import { GroupListingsModal, GroupListingsTab } from 'src/modals/GroupListings';
import { Event, Listing, ListingGroup } from 'src/WebApiController';

/**
 * Manages scroll-to-group and autopricing navigation after a new listing group
 * is created from the "Move To" bulk action. Watches the NewGroupScrollContext
 * for a target group UUID, finds the matching group row in the listings data,
 * and returns the row ID for the table to scroll to.
 */
export const useNewGroupScroll = ({
  listings,
  event,
  onItemSelectionsChanged,
}: {
  listings: Listing[] | undefined;
  event: Event;
  onItemSelectionsChanged?: (ids: number[]) => void;
}) => {
  const newGroupId = useNewGroupId();
  const setNewGroupId = useSetNewGroupId();
  const openAutoPricingForGroupId = useOpenAutoPricingForGroupId();
  const setOpenAutoPricingForGroupId = useSetOpenAutoPricingForGroupId();
  const { setModal } = useContext(ModalContext);

  // --- Scroll + Select ---
  const [scrollTargetRowId, setScrollTargetRowId] = useState<
    string | undefined
  >();
  const scrolledToGroupRef = useRef<string | null>(null);

  useEffect(() => {
    if (!newGroupId) {
      scrolledToGroupRef.current = null;
      setScrollTargetRowId(undefined);
      return;
    }

    if (scrolledToGroupRef.current === newGroupId) {
      return;
    }

    const group = listings?.find(
      (l) => l.isLtGrp && (l as ListingGroup).ltGrpId === newGroupId
    );

    if (group) {
      scrolledToGroupRef.current = newGroupId;
      setScrollTargetRowId(group.id.toString());
      onItemSelectionsChanged?.([group.id]);
      setTimeout(() => setNewGroupId(null), 3000);
    }
  }, [newGroupId, listings, setNewGroupId, onItemSelectionsChanged]);

  // --- Autopricing navigation ---
  const autoPricingGroup = useMemo(() => {
    if (!openAutoPricingForGroupId || !listings) {
      return undefined;
    }
    return listings.find(
      (l) =>
        l.isLtGrp && (l as ListingGroup).ltGrpId === openAutoPricingForGroupId
    ) as ListingGroup | undefined;
  }, [openAutoPricingForGroupId, listings]);

  const [prevAutoPricingGroupId, setPrevAutoPricingGroupId] = useState<
    string | undefined
  >();
  useEffect(() => {
    if (
      autoPricingGroup &&
      autoPricingGroup.ltGrpId &&
      autoPricingGroup.ltGrpId !== prevAutoPricingGroupId
    ) {
      setPrevAutoPricingGroupId(autoPricingGroup.ltGrpId);
      setOpenAutoPricingForGroupId(null);
      setModal({
        children: (
          <GroupListingsModal
            events={[event]}
            listingGroup={autoPricingGroup}
            initialTab={GroupListingsTab.AutoPricing}
          />
        ),
        clickOutsideToClose: true,
        size: 'slide-in',
      });
    }
  }, [
    autoPricingGroup,
    event,
    prevAutoPricingGroupId,
    setModal,
    setOpenAutoPricingForGroupId,
  ]);

  return { scrollTargetRowId };
};
